import React, { useState } from 'react'
import { Helmet } from 'react-helmet'
import {
  Box, SxProps, styled, Typography, Collapse, useTheme
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMinus } from '@fortawesome/free-solid-svg-icons/faMinus'

import { PortableText } from '../PortableText'
import { useMediaQueries } from '../../hooks'

const Content = styled(Box)`
  flex-direction: column;
  display: flex;
  max-width: 1024px;
  margin: 0 auto;

  @media only screen and (min-width: 1024px) {
    flex-direction: row;
  }
`

const PlusMinusIcon = styled(Box)<{active: boolean}>`
  position: relative;
  ${({ theme, active }) => `
    margin-right: ${theme.spacing(2)};
    width: ${theme.spacing(4)};
    height: 20px;
  
  svg {
    color: ${theme.palette.success.main};
    transition: ${theme.transitions.create(['transform'], {duration: theme.transitions.duration.standard })};
    position: absolute;
    top: 0;
    &:first-child {
      transform: rotate(${active ? '0' : '90deg'});
    }
    &:last-child {
      transform: rotate(${active ? '0' : '180deg'});
    }
  }
  `}
`

/*
 * This component renders the FAQs in different parts of website.
 * Examples: integration detail page, pricing, etc.
 */

export type FAQProps = {
  title: string
  subTitle: string
  // Block content from sanity containing questions and answers
  faqData: { title: string; content: { children: { text: string }[] }[] }[]
  containerClasses?: string // Additional classes for styling
  sx?: SxProps;
}

export const FAQ = ({
  title,
  subTitle,
  faqData, 
  containerClasses, 
  sx // @mui style props
}: FAQProps) => {
  const { palette, spacing } = useTheme();

  const { isLaptopAndLarger, isTabletAndLarger } = useMediaQueries();

  const [activeQuestions, setActiveQuestions] = useState<string[]>([])

  const toggleActive = (title: string) => () => {
    if (activeQuestions.includes(title)) {
      setActiveQuestions(activeQuestions.filter(x => x !== title))
    } else {
      setActiveQuestions([...activeQuestions, title])
    }
  }

  /* Preparing structured data using faqData */
  const structuredData: Array<{
    '@type': string
    name: string
    acceptedAnswer: object
  }> = []
  faqData.forEach(item => {
    structuredData.push({
      '@type': 'Question',
      name: item.title,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.content
          .map(({ children }) => children.map(({ text }) => text).join(''))
          .join(' ')
      }
    })
  })

  return (
    <Box
      component="section"
      className={containerClasses}
      sx={{
        background: palette.background[palette.mode === 'dark' ? 'default' : 'paper'],
        p: 2,
        pt: 6,
        ...(isTabletAndLarger && { pt: 16, pb: 12}),
        ...sx,
      }}>
      {/* Adding FAQPage structured data to the head tag */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: structuredData
          })}
        </script>
      </Helmet>

      <Content>
        <Box>
          <Typography variant="h2">{title}</Typography>
          <Typography variant="h4" sx={{pt: 1}}>{subTitle}</Typography>
        </Box>

        <Box flex={1} display="flex" flexDirection="column"
          sx={{
            mt: 4,
            ml: 0,
            ...(isLaptopAndLarger && {
              mt: 1, ml: 6
            })
          }}>
          {faqData.map(item => {
            const isActive = activeQuestions.includes(item.title);
            return (
              <Box
                key={item.title}
                display="flex"
                flexDirection="column"
                sx={{ mb: 4, cursor: 'pointer' }}
                onClick={toggleActive(item.title)}>
                <Box display="flex" alignItems="center">
                  <PlusMinusIcon active={isActive}>
                    <Box component={FontAwesomeIcon} icon={faMinus} size="lg" />
                    <Box component={FontAwesomeIcon} icon={faMinus} size="lg" />
                  </PlusMinusIcon>
                  <Typography sx={{ flex: 1 }} variant="h5" fontWeight="bold">{item.title}</Typography>
                </Box>
                <Collapse in={isActive}>
                  <Typography color="text.secondary" sx={{
                    pt: 4,
                    pl: 6,
                    'p': {
                      paddingTop: spacing(1),
                      fontSize: '18px'
                    }
                  }}>
                    <PortableText
                      blocks={item.content}
                      trackSectionHeader={title}
                    />
                  </Typography>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      </Content>
    </Box>
  )
}
